import { useMemo } from 'react'
import type { CustomerPrivacyDashboard } from '../../customerPrivacyApi'
import { formatDateTime } from '../../helpers/formatters'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import { buildCustomerAreaNavItems } from '../customer-area'
import {
  resolveExportStateLabel,
  resolveLatestPrivacyRequest,
  resolvePrivacyAccountStatusLabel,
} from './privacyPageShared'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type UseCustomerPrivacyPageControllerParams = {
  dashboard: CustomerPrivacyDashboard | null
  language: ShopLanguage
  locale: ShopLocale
  t: TranslateFn
  toLocalizedPath: (path: string) => string
}

export const useCustomerPrivacyPageController = ({
  dashboard,
  language,
  locale,
  t,
  toLocalizedPath,
}: UseCustomerPrivacyPageControllerParams) => {
  const navItems = useMemo(
    () =>
      buildCustomerAreaNavItems({
        t,
        toLocalizedPath,
        language,
      }),
    [language, t, toLocalizedPath],
  )

  const latestRequest = useMemo(
    () => (dashboard ? resolveLatestPrivacyRequest(dashboard) : null),
    [dashboard],
  )

  const accountStatusLabel = useMemo(
    () => (dashboard ? resolvePrivacyAccountStatusLabel(dashboard.account.accountStatus, t) : ''),
    [dashboard, t],
  )

  const exportStateLabel = useMemo(
    () => (dashboard ? resolveExportStateLabel(dashboard.exportState.state, t) : ''),
    [dashboard, t],
  )

  const readyToDownload = useMemo(
    () =>
      Boolean(
        dashboard &&
          dashboard.exportState.state === 'ready' &&
          dashboard.exportState.requestId &&
          dashboard.exportState.downloadUrl,
      ),
    [dashboard],
  )

  const privacyUpdatedAtValue = useMemo(
    () =>
      dashboard
        ? formatDateTime(dashboard.account.privacyUpdatedAt, locale, t('common.never'))
        : t('common.never'),
    [dashboard, locale, t],
  )

  return {
    navItems,
    latestRequest,
    accountStatusLabel,
    exportStateLabel,
    readyToDownload,
    privacyUpdatedAtValue,
  }
}
